import React, { useState, useEffect, useRef } from 'react';
import { auth, firestore } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import './WorldDbCreator.css';

// Contact creator component that can be used in multiple places
const WorldDbCreator = ({ onClose, initialFields = {} }) => {
  const [userId, setUserId] = useState(null);
  const [fields, setFields] = useState({
    ...initialFields
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const modalRef = useRef(null);

  // Available fields for the dropdown
  const availableFields = {
    lead_firstname: 'First Name',
    lead_lastname: 'Last Name',
    lead_email: 'Email',
    lead_telephone: 'Telephone',
    lead_address: 'Address',
    lead_city: 'City',
    lead_postcode: 'Postcode',
    lead_country: 'Country',
    business_name: 'Business Name',
    lead_website: 'Website',
    lead_instagram: 'Instagram',
    lead_linkedin: 'LinkedIn',
    lead_tiktok: 'TikTok',
    lead_facebook: 'Facebook',
    lead_rating: 'Total Rating',
    lead_reviews: 'Total Reviews',
    lead_stars: 'Stars',
    lead_link: 'Link',
    lead_image: 'Image URL',
    lead_description: 'Description',
    lead_type: 'Type',
    lead_mapsurl: 'Google Maps URL'
  };
  
  // Required fields that must be filled
  const requiredFields = ['lead_email'];
  
  // Get user ID via Firebase Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });
    return () => unsubscribe();
  }, []);
  
  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Update a field value
  const handleFieldChange = (fieldName, value) => {
    setFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Validate form before submission
  const validateForm = () => {
    // Check if required fields are present
    for (const field of requiredFields) {
      if (!fields[field] || fields[field].trim() === '') {
        setError(`${availableFields[field]} is required`);
        return false;
      }
    }
    
    // Email validation
    if (fields.lead_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.lead_email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    return true;
  };

  // Save the contact to the database
  const handleSaveContact = async () => {
    if (!validateForm()) return;
    
    // Clear previous states
    setError('');
    setLoading(true);
    
    try {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Prepare the data with timestamp and import name
      const contactData = {
        ...fields,
        timestamp: new Date().toISOString(),
        import_name: 'manual_entry', // Tag for manually created contacts
        created_at: new Date().toISOString()
      };
      
      // Save to Firestore
      const dbCollection = collection(firestore, `World/${userId}/db`);
      await addDoc(dbCollection, contactData);
      
      setSuccess(true);
      setLoading(false);
      
      // Close after successful submission (with a small delay for feedback)
      setTimeout(() => onClose(), 1500);
      
    } catch (error) {
      console.error('Error saving contact:', error);
      setError('Failed to save contact. Please try again.');
      setLoading(false);
    }
  };

  // Add a new field to the form
  const handleAddField = () => {
    // Find a field that's not already in use
    const unusedFields = Object.keys(availableFields).filter(
      field => !Object.keys(fields).includes(field)
    );
    
    if (unusedFields.length > 0) {
      const fieldToAdd = unusedFields[0];
      setFields(prev => ({
        ...prev,
        [fieldToAdd]: ''
      }));
    }
  };

  // Remove a field from the form
  const handleRemoveField = (fieldName) => {
    if (requiredFields.includes(fieldName)) {
      setError(`${availableFields[fieldName]} is a required field and cannot be removed`);
      return;
    }
    
    setFields(prev => {
      const newFields = { ...prev };
      delete newFields[fieldName];
      return newFields;
    });
  };

  // Get fields that can still be added
  const getAvailableFieldsToAdd = () => {
    return Object.keys(availableFields).filter(
      field => !Object.keys(fields).includes(field)
    );
  };

  // Add a specific field from dropdown selection
  const handleAddSelectedField = (fieldName) => {
    if (fieldName && availableFields[fieldName] && !fields[fieldName]) {
      setFields(prev => ({
        ...prev,
        [fieldName]: ''
      }));
      return true;
    }
    return false;
  };

  return (
    <div className="world-db-creator-overlay">
      <div className="world-db-creator-container" ref={modalRef}>
        <button 
          className="world-db-creator-close-button" 
          onClick={onClose}
          type="button"
        >
          ✕
        </button>
        
        <h2 className="world-db-creator-title">
          {success ? "Contact Added!" : "Add New Contact"}
        </h2>
        
        {success ? (
          <div className="world-db-creator-success">
            <div className="world-db-creator-success-icon">✓</div>
            <p>Contact has been successfully added to your database</p>
          </div>
        ) : (
          <>
            <p className="world-db-creator-description">
              Fill in the contact details below. Fields marked with * are required.
            </p>
            
            {error && <div className="world-db-creator-error">{error}</div>}
            
            <div className="world-db-creator-fields">
              {Object.keys(fields).map((fieldName) => (
                <div className="world-db-creator-field-row" key={fieldName}>
                  <div className="world-db-creator-field-header">
                    <label className="world-db-creator-field-label">
                      {availableFields[fieldName]}
                      {requiredFields.includes(fieldName) && <span className="required">*</span>}
                    </label>
                    
                    {!requiredFields.includes(fieldName) && (
                      <button 
                        type="button"
                        className="world-db-creator-remove-field" 
                        onClick={() => handleRemoveField(fieldName)}
                        title="Remove field"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  
                  <input
                    type={fieldName === 'lead_email' ? 'email' : 'text'}
                    className="world-db-creator-input"
                    value={fields[fieldName] || ''}
                    onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                    placeholder={`Enter ${availableFields[fieldName]?.toLowerCase() || fieldName}`}
                    required={requiredFields.includes(fieldName)}
                  />
                </div>
              ))}
            </div>
            
            {getAvailableFieldsToAdd().length > 0 && (
              <div className="world-db-creator-add-field-wrapper">
                <select 
                  className="world-db-creator-field-selector"
                  id="fieldSelector"
                >
                  <option value="">Select a field to add...</option>
                  {getAvailableFieldsToAdd().map(field => (
                    <option key={field} value={field}>
                      {availableFields[field]}
                    </option>
                  ))}
                </select>
                
                <button 
                  type="button"
                  className="world-db-creator-add-field" 
                  onClick={() => {
                    const select = document.getElementById('fieldSelector');
                    if (select && select.value) {
                      if (handleAddSelectedField(select.value)) {
                        select.value = ''; // Reset only if field was added
                      }
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Add Field
                </button>
              </div>
            )}
            
            <div className="world-db-creator-actions">
              <button 
                type="button"
                className="world-db-creator-cancel" 
                onClick={onClose}
              >
                Cancel
              </button>
              <button 
                type="button"
                className={`world-db-creator-save ${loading ? 'loading' : ''}`}
                onClick={handleSaveContact}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Contact'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WorldDbCreator; 